import { Temporal } from '@js-temporal/polyfill';
import { AutoUser } from './auto-user';
import { BinaryTree, BTTYPE } from './binary-tree';
import { CoordinatorService } from './coordinator.service';
import { Dataset } from './dataset';
import { Keyword } from './keyword';
import { ProjectMember } from './project-member';
import { ScriptAttachment } from './script-attachment';
import { ScriptComment } from './script-comment';
import { ScriptHitset } from './script-hitset';
import { ScriptOutput } from './script-output';
import { ScriptProject } from './script-project';
import { ScriptReport } from './script-report';
import { View } from './view';

type SUBSET_ARG = string | View;
type KEYWORD_ARG = string | Keyword;
type BINARY_TREE_ARG = string | BinaryTree;
type COMMENT_ARG = string | ProjectMember | AutoUser;

const reportProjectFields = [
    'title',
    'client',
    'clientContact',
    'ntkResearcher',
    'startDate',
    'deliveryDate',
    'keyWords',
    'description',
    'limitation',
    'notes',
];

export type REPORT_FIELDS = (typeof reportProjectFields)[number];

type ROW_CHANGE_ARG = { tag: REPORT_FIELDS; value: any };

export class Report {
    id: string;

    includeDatabins = false;
    isSaving = false;

    private _linkExpiryDate: Date;
    linkPath = '';

    private _valueMap: Map<string, any>;

    private _itemSource: Dataset | View | ScriptHitset;
    private _subsetSet = new Set<View>();
    private _keywordSet = new Set<Keyword>();
    private _binaryTreeSet = new Set<BinaryTree>();
    private _commentSet = new Set<'current' | ProjectMember | AutoUser>();
    private _attachmentSet = new Set<'current' | ProjectMember | AutoUser>();

    constructor(
        public coords: CoordinatorService,
        params?: { [tag: string]: any },
    ) {
        const mp =
            params && typeof params === 'object' ? new Map(Object.entries(params)) : undefined;
        this._valueMap = mp || new Map();
        if (!this.title) {
            this._valueMap = new Map(this.coords.project$.current?.manifestParams.valueMap);
            this._valueMap.set('title', `Report - ${this.coords.project$.title}`);
            this._subsetSet = new Set(this.coords.view$.sortedViewLs);
            this._keywordSet = new Set(this.coords.keyword$.keywordLs);
            this._binaryTreeSet = new Set(this.coords.binaryTree$.treeLs);
            this._commentSet.add('current');
            this._attachmentSet.add('current');
        }
        this.id = this.coords.uniqueId();
        this._itemSource = this.coords.dataset$.main;

        this._linkExpiryDate = this.coords.utils$.instant2date(
            Temporal.Now.instant().add({ hours: 48 }),
        )!;
    }

    get dataMap() {
        const mp = new Map<string, any>(this._valueMap);
        const s = new Set(mp.keys());
        reportProjectFields.forEach((field) => {
            if (!s.has(field)) mp.set(field, '');
        });

        mp.delete('ownerId');
        return mp;
    }

    get rowChangeFunction() {
        return (arg: ROW_CHANGE_ARG) => this._rowChangeFunction(arg);
    }

    in(arg: View | Keyword | BinaryTree) {
        if (arg instanceof View) return this._subsetSet.has(arg);
        else if (arg instanceof Keyword) return this._keywordSet.has(arg);
        else if (arg instanceof BinaryTree) return this._binaryTreeSet.has(arg);
        else {
            switch (arg) {
                case 'comments':
                    return this._commentSet.has('current');
                case 'attachments':
                    return this._attachmentSet.has('current');
                default:
                    return false;
            }
        }
    }

    switch(arg: View | Keyword | BinaryTree | string, val?: boolean) {
        let s: Set<any>;
        if (arg instanceof View) s = this._subsetSet;
        else if (arg instanceof Keyword) s = this._keywordSet;
        else if (arg instanceof BinaryTree) s = this._binaryTreeSet;
        else {
            switch (arg) {
                case 'comments':
                    s = this._commentSet;
                    break;
                case 'attachments':
                    s = this._attachmentSet;
                default:
                    return;
            }
            arg = 'current';
        }
        s = s!;
        if (!s) return;
        if (val === undefined) {
            if (s.has(arg)) s.delete(arg);
            else s.add(arg);
        } else if (val) s.add(arg);
        else s.delete(arg);
    }

    private _rowChangeFunction({ tag, value }: ROW_CHANGE_ARG) {
        this._valueMap.set(tag, value);
    }

    // run(tp: REPORT_TYPE, responder?: Publication) {
    //     console.log('Running report:', this.title);
    // }

    async hubExport({
        isLocalSave,
        includeDatabins,
    }: {
        isLocalSave?: boolean;
        includeDatabins?: boolean;
    } = {}) {
        const out = new ScriptOutput(this.coords);
        out.title = this.title;

        out.addProject(new ScriptProject(this.coords, this));
        out.addItems(Array.from(this._itemSource.idSet));
        out.addSubsets(this.subsets);
        out.addKeywords(this.keywords);

        out.addAttachments(this._getSaveAttachments());
        (includeDatabins || this.includeDatabins) &&
            out.addDatabins(this.coords.project$.current!.scriptClass.databins);
        this._binaryTreeSet.forEach((bt) => {
            if (bt.type === 'ratings') out.addRatings(bt.scriptClass);
            else if (bt.type === 'tags') out.addTags(bt.scriptClass);
        });

        const um = this.coords.script$.project!.userManager;

        // Get currentAutouserSnapshot first to check its ID
        const currentSnapshot = um.currentAutouserSnapshot;
        const snapshotId = currentSnapshot.id;
        out.addAutousers([currentSnapshot]);

        const othersList = um.othersAsAutouserList();
        // Filter out any autousers that might be the same as currentSnapshot (safety check)
        const filteredOthersList = othersList.filter((au) => au.id !== snapshotId);
        out.addAutousers(filteredOthersList);

        const managerList = this.coords.script$.project!.autouserManager.list();
        // Filter out any autousers that might be the same as currentSnapshot (safety check)
        const filteredManagerList = managerList.filter((au) => au.id !== snapshotId);
        out.addAutousers(filteredManagerList);

        if (isLocalSave) {
            await out.save();
            return;
        }
        const payload = await out.save({ returnBlob: true });
        return payload;
    }

    async save({ includeDatabins }: { includeDatabins: boolean } = { includeDatabins: false }) {
        this.isSaving = true;
        this.coords.display$.markForCheck();
        try {
            await this.hubExport({ isLocalSave: true, includeDatabins });
        } finally {
            this.isSaving = false;
            this.coords.display$.markForCheck();
        }
    }

    async saveLink(params?: any) {
        this.isSaving = true;
        this.coords.display$.markForCheck();
        try {
            const blob = await this.hubExport(params);
            if (!blob) return;

            const code = this.coords.uniqueId();
            const rPth = `/userdata/${this.coords.user$.currentId}/public/${code}`;

            const prefix = `${location.protocol}//${location.host}/pid/`;

            const exp = this.coords.utils$.date2instant(this._linkExpiryDate)!;
            const tz = this.coords.utils$.timezone;
            const pdt = exp.toZonedDateTimeISO(tz).toPlainDate();
            const nextDay = pdt.add({ days: 1 });
            const midnight = nextDay
                .toPlainDateTime({ hour: 0, minute: 0, second: 0 })
                .toZonedDateTime(tz);

            const fstParams = {
                id: code,
                groupId: this.id,
                userId: this.coords.user$.currentId,
                type: 'report-project',
                expiration: midnight.toInstant().toString(),
                sourceProjectId: this.coords.project$.currentId,
                remotePath: rPth,
            };

            const resp = await this.coords.firebase$.firestore.addDocument(
                'public',
                fstParams.id,
                fstParams,
            );
            if (resp) {
                const resp2 = await this.coords.firebase$.storage.uploadBlob(rPth, blob);

                if (resp2) {
                    this.linkPath = `${prefix}${fstParams.id}`;
                    this.coords.display$.markForCheck();
                }
            }
            this.coords.display$.markForCheck();
        } finally {
            this.isSaving = false;
            this.coords.display$.markForCheck();
        }
    }

    clearLink() {
        this.linkPath = '';
    }

    dialog() {
        this.linkPath = '';
        this.coords.display$.dialog$.next({
            action: 'report-project',
            rp: this,
        });
    }

    get title() {
        return this._valueMap.get('title');
    }

    get description() {
        return this._valueMap.get('description');
    }

    private _getSubset(arg: SUBSET_ARG) {
        if (arg instanceof View) return arg;

        const v = this.coords.view$.get(arg);
        if (v) return v;
        return this.coords.view$.getByTitle(arg);
    }

    json() {
        return this.coords.utils$.toJSONready(this._valueMap);
    }

    get valueMap() {
        return new Map(this._valueMap);
    }

    get(tag: string) {
        if (tag == 'id') return this.id;
        return this._valueMap.get(tag);
    }

    set(tag: string, val: any) {
        if (this.get(tag) === val || tag === 'id') return;
        if (tag === 'title' && !val) return;
        this._valueMap.set(tag, val);
    }

    selectItemSource(arg: SUBSET_ARG | ScriptHitset) {
        if (arg instanceof ScriptHitset) this._itemSource = arg;
        else {
            const s = this._getSubset(arg);
            if (s) this._itemSource = s;
            else this._itemSource = this.coords.dataset$.main;
        }
    }

    get itemSource() {
        if (!this._itemSource) this._itemSource = this.coords.dataset$.main;
        return this._itemSource;
    }

    set itemSource(val: Dataset | View | ScriptHitset) {
        this._itemSource = val;
    }

    addComments(arg: COMMENT_ARG | COMMENT_ARG[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((c) => {
            if (c === 'current') this._commentSet.add('current');
            else if (c instanceof ProjectMember || c instanceof AutoUser) this._commentSet.add(c);
            else {
                console.log('IMPLEMENT GET PM/AUTO USER', c);
            }
        });
    }

    addAttachments(arg: COMMENT_ARG | COMMENT_ARG[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((c) => {
            if (c === 'current') this._attachmentSet.add('current');
        });
    }

    addSubsets(arg: SUBSET_ARG | SUBSET_ARG[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((s) => {
            const v = this._getSubset(s);
            if (v) this._subsetSet.add(v);
        });
    }

    deleteSubsets(arg: SUBSET_ARG | SUBSET_ARG[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((s) => {
            const v = this._getSubset(s);
            if (v) this._subsetSet.delete(v);
        });
    }

    private _getBinaryTree(tp: BTTYPE, arg: BINARY_TREE_ARG) {
        if (arg instanceof BinaryTree) return arg;
        return this.coords.binaryTree$.getByTitle(tp, arg);
    }

    addBinaryTree(tp: BTTYPE, arg: BINARY_TREE_ARG | BINARY_TREE_ARG[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((b) => {
            const bt = this._getBinaryTree(tp, b);
            if (bt) this._binaryTreeSet.add(bt);
        });
    }

    private _getKeyword(arg: KEYWORD_ARG) {
        if (arg instanceof Keyword) return arg;
        return this.coords.keyword$.getBySource(arg);
    }

    get keywords() {
        return Array.from(this._keywordSet).map((k) => k.scriptClass);
    }

    get subsets() {
        return Array.from(this._subsetSet).map((v) => v.scriptClass);
    }

    addKeywords(arg: KEYWORD_ARG | KEYWORD_ARG[]) {
        if (!Array.isArray(arg)) arg = [arg];
        arg.forEach((k) => {
            const kw = this._getKeyword(k);
            if (kw) {
                this._keywordSet.add(kw);
            }
        });
    }

    private _getSaveComments() {
        const ls: ScriptComment[] = [];
        if (this._commentSet.has('current'))
            this.coords.comment$.currentUserCommentLs.forEach((c) => ls.push(c.scriptClass));
        return ls;
    }

    private _getSaveAttachments() {
        const ls: ScriptAttachment[] = [];
        if (this._attachmentSet.has('current'))
            this.coords.attachment$.attachments.forEach((att) => ls.push(att.scriptClass));
        return ls;
    }

    get scriptClass() {
        return new ScriptReport(this.coords, this);
    }

    get linkExpiryDate() {
        return this._linkExpiryDate;
    }

    set linkExpiryDate(val: Date) {
        this._linkExpiryDate = val;
    }
}
